import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/EditOutlined";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  getPipelineWeightConfigByRunId,
  getPaginatedValidationRunScoringConfigs,
  getPaginatedComparisonRunScoringConfigs,
  getPaginatedResearchQueryRunScoringConfigs,
  getPaginatedValidationScoringConfigs,
  getPaginatedComparisonScoringConfigs,
  getPaginatedGenericRQScoringConfigs,
  updatePipelineWeightConfig,
  createPipelineWeightConfig,
  batchCreateOrUpdateRunScoringConfig,
  batchCreateOrUpdateComparisonRunScoringConfig,
  batchCreateOrUpdateResearchQueryRunScoringConfig,
} from "../../services/scoringService";
import { getRunInstanceDetail } from "../../services/runInstance";
import { getAllResourceList } from "../../services/resourcesService";
import { getRulesList } from "../../services/rulesService";
import { getResearchQueriesList } from "../../services/ResearchQueriesService";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import {
  getFormattedDateTime,
  useToast,
  extractApiErrorMessage,
} from "../../services/utils";
import { scoringSeverityOptions } from "../../services/constants";
import {
  PIPELINE_COLORS,
  PIPELINE_LABELS,
} from "../../components/Scoring/scoreTheme";

type PipelineKey = "validation" | "comparison" | "research_query";

const SYSTEM_DEFAULT_SEVERITY = "medium";
const SYSTEM_DEFAULT_FACTOR = "1.0";

interface IProps {
  runInstanceId?: number;
}

const SeverityChip: React.FC<{ severity?: string }> = ({ severity }) => {
  if (!severity) return <span>—</span>;
  const s = severity.toLowerCase();
  const palette: Record<string, { bg: string; color: string }> = {
    low: { bg: "#fff8e1", color: "#f57f17" },
    medium: { bg: "#fff3e0", color: "#e65100" },
    high: { bg: "#ffebee", color: "#c62828" },
  };
  const p = palette[s] ?? { bg: "#eceff1", color: "#455a64" };
  return (
    <Chip
      label={severity.toUpperCase()}
      size="small"
      sx={{ backgroundColor: p.bg, color: p.color, fontWeight: 600 }}
    />
  );
};

const field = (label: string, value: any, md: number = 4) => (
  <Grid item xs={12} sm={6} md={md}>
    <label className="label-text">{label}</label>
    <div className="form-control">
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </Grid>
);

const PipelineHeader: React.FC<{
  pipelineKey: "validation" | "comparison" | "research_query";
  count: number;
}> = ({ pipelineKey, count }) => (
  <Stack direction="row" alignItems="center" spacing={1.5}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: PIPELINE_COLORS[pipelineKey],
      }}
    />
    <Typography sx={{ fontWeight: 600 }}>
      {PIPELINE_LABELS[pipelineKey]} Pipeline
    </Typography>
    <Chip
      size="small"
      label={`${count} ${count === 1 ? "config" : "configs"}`}
    />
  </Stack>
);

interface EntityBlockProps {
  title: string;
  runConfig: any;
  baseConfig: any;
  baseFields: Array<{ label: string; key: string }>;
  onSave: (severity: string, factor: number) => Promise<void> | void;
  saving?: boolean;
}

const EntityBlock: React.FC<EntityBlockProps> = ({
  title,
  runConfig,
  baseConfig,
  baseFields,
  onSave,
  saving,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftSeverity, setDraftSeverity] = useState<string>(
    runConfig?.severity ?? "low",
  );
  const [draftFactor, setDraftFactor] = useState<string>(
    runConfig?.factor != null ? String(runConfig.factor) : "",
  );

  const startEdit = () => {
    setDraftSeverity(runConfig?.severity ?? "low");
    setDraftFactor(runConfig?.factor != null ? String(runConfig.factor) : "");
    setIsEditing(true);
  };

  const draftFactorNumber = Number(draftFactor);
  const factorError =
    draftFactor === ""
      ? "Factor is required"
      : draftFactorNumber <= 0
        ? "Factor must be positive"
        : draftFactorNumber > 1
          ? "Factor must not exceed 1"
          : "";

  const handleSave = async () => {
    if (factorError) return;
    await onSave(draftSeverity, draftFactorNumber);
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        p: 2,
        mb: 2,
        backgroundColor: "#fafafa",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
        {!isEditing && (
          <Tooltip title="Edit run override (severity & factor only)" arrow>
            <IconButton size="small" onClick={startEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" sx={{ color: "#666" }}>
              Run Override
            </Typography>
            <Chip
              label="Editable"
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
              }}
            />
          </Stack>
        </Grid>
        {isEditing ? (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <label className="label-text">Severity</label>
              <TextField
                select
                fullWidth
                size="small"
                value={draftSeverity}
                onChange={(e) => setDraftSeverity(e.target.value)}
                className="form-control"
              >
                {scoringSeverityOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <label className="label-text">Factor</label>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={draftFactor}
                onChange={(e) => setDraftFactor(e.target.value)}
                className={`form-control${factorError ? " has-error" : ""}`}
                error={Boolean(factorError)}
                helperText={factorError || " "}
                inputProps={{ step: "0.01", min: 0, max: 1 }}
              />
            </Grid>
            {field(
              "Status",
              runConfig?.is_active === false ? "Inactive" : "Active",
            )}
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <label className="label-text">Severity</label>
              <div className="form-control">
                <SeverityChip severity={runConfig?.severity} />
              </div>
            </Grid>
            {field("Factor", runConfig?.factor)}
            {field(
              "Status",
              runConfig?.is_active === false ? "Inactive" : "Active",
            )}
          </>
        )}
      </Grid>
      {isEditing && (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          <Button
            type="button"
            className="btn-orange btn-dark btn-sm"
            variant="contained"
            startIcon={<CloseIcon />}
            onClick={() => setIsEditing(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="btn-orange btn-sm"
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleSave}
            disabled={saving || !draftSeverity || Boolean(factorError)}
          >
            Save
          </Button>
        </Stack>
      )}
      <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #ccc" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ color: "#666" }}>
            Base Config
          </Typography>
          <Chip
            label="Read only"
            size="small"
            sx={{ height: 18, fontSize: 10, color: "#666" }}
          />
        </Stack>
        {baseConfig ? (
          <Grid container rowSpacing={2} columnSpacing={2} sx={{ mt: 0.5 }}>
            {baseFields.map((bf) => field(bf.label, baseConfig[bf.key], 4))}
          </Grid>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={`System defaults in use (severity: ${SYSTEM_DEFAULT_SEVERITY}, factor: ${SYSTEM_DEFAULT_FACTOR})`}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                backgroundColor: "#fff3e0",
                color: "#e65100",
              }}
            />
            <Typography variant="body2" sx={{ mt: 1, color: "#999" }}>
              No base configuration found. Add one from the dedicated config
              page.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface AddEntityFormProps {
  entityLabel: string;
  options: Array<{ id: number; name: string }>;
  onAdd: (
    entityId: number,
    severity: string,
    factor: number,
  ) => Promise<void> | void;
  onCancel: () => void;
  saving?: boolean;
  resolveBaseConfig: (entityId: number) => any | undefined;
  getFactorForSeverity: (
    baseConfig: any,
    severity: string,
  ) => number | undefined;
}

const AddEntityForm: React.FC<AddEntityFormProps> = ({
  entityLabel,
  options,
  onAdd,
  onCancel,
  saving,
  resolveBaseConfig,
  getFactorForSeverity,
}) => {
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(
    null,
  );
  const [severity, setSeverity] = useState<string>(SYSTEM_DEFAULT_SEVERITY);
  const [factor, setFactor] = useState<string>("");
  const [prefillSource, setPrefillSource] = useState<"base" | "default" | null>(
    null,
  );

  // When the selected entity changes, look up its base config and prefill
  // severity + factor from the base, falling back to system defaults.
  useEffect(() => {
    if (!selected) {
      setSeverity(SYSTEM_DEFAULT_SEVERITY);
      setFactor("");
      setPrefillSource(null);
      return;
    }
    const base = resolveBaseConfig(selected.id);
    const nextSeverity = SYSTEM_DEFAULT_SEVERITY;
    if (base) {
      const baseFactor = getFactorForSeverity(base, nextSeverity);
      setSeverity(nextSeverity);
      setFactor(
        baseFactor != null && !Number.isNaN(baseFactor)
          ? String(baseFactor)
          : SYSTEM_DEFAULT_FACTOR,
      );
      setPrefillSource("base");
    } else {
      setSeverity(nextSeverity);
      setFactor(SYSTEM_DEFAULT_FACTOR);
      setPrefillSource("default");
    }
  }, [selected, resolveBaseConfig, getFactorForSeverity]);

  // When severity changes and a base config exists, re-derive factor from
  // the base for the new severity bucket.
  const handleSeverityChange = (next: string) => {
    setSeverity(next);
    if (!selected) return;
    const base = resolveBaseConfig(selected.id);
    if (base) {
      const baseFactor = getFactorForSeverity(base, next);
      if (baseFactor != null && !Number.isNaN(baseFactor)) {
        setFactor(String(baseFactor));
      }
    }
  };

  const factorNumber = Number(factor);
  const factorError =
    factor === ""
      ? "Factor is required"
      : factorNumber <= 0
        ? "Factor must be positive"
        : factorNumber > 1
          ? "Factor must not exceed 1"
          : "";

  const handleSave = async () => {
    if (!selected || !severity || factorError) return;
    await onAdd(selected.id, severity, factorNumber);
    setSelected(null);
    setSeverity(SYSTEM_DEFAULT_SEVERITY);
    setFactor("");
    setPrefillSource(null);
  };

  return (
    <Box
      sx={{
        border: "1px dashed #90caf9",
        borderRadius: 1,
        p: 2,
        mb: 2,
        backgroundColor: "#f5fbff",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>
          Add {entityLabel} Configuration
        </Typography>
        {prefillSource === "base" && (
          <Chip
            label="Prefilled from base config"
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
            }}
          />
        )}
        {prefillSource === "default" && (
          <Chip
            label="Using system defaults (no base config)"
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              backgroundColor: "#fff3e0",
              color: "#e65100",
            }}
          />
        )}
      </Stack>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <label className="label-text">{entityLabel}</label>
          <Autocomplete
            size="small"
            options={options}
            getOptionLabel={(opt) => opt.name ?? `#${opt.id}`}
            value={selected}
            onChange={(_e, v) => setSelected(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Select ${entityLabel.toLowerCase()}`}
                className="form-control"
              />
            )}
            isOptionEqualToValue={(o, v) => o.id === v.id}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <label className="label-text">Severity</label>
          <TextField
            select
            fullWidth
            size="small"
            value={severity}
            onChange={(e) => handleSeverityChange(e.target.value)}
            className="form-control"
          >
            {scoringSeverityOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <label className="label-text">Factor</label>
          <TextField
            type="number"
            fullWidth
            size="small"
            value={factor}
            onChange={(e) => setFactor(e.target.value)}
            className={`form-control${factorError ? " has-error" : ""}`}
            error={Boolean(factorError)}
            helperText={factorError || " "}
            inputProps={{ step: "0.01", min: 0, max: 1 }}
          />
        </Grid>
      </Grid>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Button
          type="button"
          className="btn-orange btn-dark btn-sm"
          variant="contained"
          startIcon={<CloseIcon />}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="btn-orange btn-sm"
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving || !selected || !severity || Boolean(factorError)}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
};

const RunScoringDetailedOverview: React.FC<IProps> = ({ runInstanceId }) => {
  const params = useParams();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();

  const resolvedRunId =
    runInstanceId ??
    (params.runInstanceId ? parseInt(params.runInstanceId) : undefined) ??
    (params.id ? parseInt(params.id) : undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [runDetail, setRunDetail] = useState<any>(null);
  const [pipelineWeights, setPipelineWeights] = useState<any>(null);
  const [validationRunConfigs, setValidationRunConfigs] = useState<any[]>([]);
  const [comparisonRunConfigs, setComparisonRunConfigs] = useState<any[]>([]);
  const [rqRunConfigs, setRqRunConfigs] = useState<any[]>([]);
  const [validationBaseByResource, setValidationBaseByResource] = useState<
    Map<number, any>
  >(new Map());
  const [comparisonBaseByRule, setComparisonBaseByRule] = useState<
    Map<number, any>
  >(new Map());
  const [rqBaseByQuery, setRqBaseByQuery] = useState<Map<number, any>>(
    new Map(),
  );
  const [resourceMap, setResourceMap] = useState<Map<number, string>>(
    new Map(),
  );
  const [ruleMap, setRuleMap] = useState<Map<number, string>>(new Map());
  const [grqMap, setGrqMap] = useState<Map<number, string>>(new Map());

  // Edit-state for pipeline weights
  const [editingWeights, setEditingWeights] = useState(false);
  const [weightDraft, setWeightDraft] = useState({
    validation_weight: "",
    comparison_weight: "",
    research_query_weight: "",
  });
  const [savingWeights, setSavingWeights] = useState(false);

  // Per-pipeline "add new" form open state and per-row save flag
  const [addingPipeline, setAddingPipeline] = useState<PipelineKey | null>(
    null,
  );
  const [savingEntity, setSavingEntity] = useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name:
        runDetail?.run_name ||
        (resolvedRunId ? `Run #${resolvedRunId}` : "Run"),
      id: resolvedRunId ?? null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [resolvedRunId, runDetail?.run_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = useCallback(() => {
    if (!resolvedRunId) return;
    setIsLoading(true);
    return Promise.all([
      getRunInstanceDetail({ currentRunInstanceId: resolvedRunId }).catch(
        () => null,
      ),
      getPipelineWeightConfigByRunId(resolvedRunId).catch(() => null),
      getPaginatedValidationRunScoringConfigs({
        page: 1,
        pSize: 1000,
        runInstanceId: resolvedRunId,
        isActive: true,
      }).catch(() => null),
      getPaginatedComparisonRunScoringConfigs({
        page: 1,
        pSize: 1000,
        runInstanceId: resolvedRunId,
        isActive: true,
      }).catch(() => null),
      getPaginatedResearchQueryRunScoringConfigs({
        page: 1,
        pSize: 1000,
        runInstanceId: resolvedRunId,
        isActive: true,
      }).catch(() => null),
      getPaginatedValidationScoringConfigs({
        page: 1,
        pSize: 1000,
        isActive: true,
      }).catch(() => null),
      getPaginatedComparisonScoringConfigs({
        page: 1,
        pSize: 1000,
        isActive: true,
      }).catch(() => null),
      getPaginatedGenericRQScoringConfigs({
        page: 1,
        pSize: 1000,
        isActive: true,
      }).catch(() => null),
      getAllResourceList().catch(() => []),
      getRulesList({ is_active: true }).catch(() => []),
      getResearchQueriesList({ page: 1, pSize: 10000, isActive: "true" }).catch(
        () => null,
      ),
    ])
      .then(
        ([
          run,
          weights,
          vRun,
          cRun,
          rqRun,
          vBase,
          cBase,
          rqBase,
          resources,
          rules,
          rqList,
        ]) => {
          setRunDetail(run);
          setPipelineWeights(weights);
          setValidationRunConfigs(vRun?.items || []);
          setComparisonRunConfigs(cRun?.items || []);
          setRqRunConfigs(rqRun?.items || []);

          const vMap = new Map<number, any>();
          (vBase?.items || []).forEach((b: any) => {
            if (b.resource_id != null) vMap.set(b.resource_id, b);
          });
          setValidationBaseByResource(vMap);

          const cMap = new Map<number, any>();
          (cBase?.items || []).forEach((b: any) => {
            if (b.rule_id != null) cMap.set(b.rule_id, b);
          });
          setComparisonBaseByRule(cMap);

          const rqMap = new Map<number, any>();
          (rqBase?.items || []).forEach((b: any) => {
            if (b.generic_research_query_id != null)
              rqMap.set(b.generic_research_query_id, b);
          });
          setRqBaseByQuery(rqMap);

          const rMap = new Map<number, string>();
          (Array.isArray(resources) ? resources : []).forEach((r: any) => {
            if (r.id != null) rMap.set(r.id, r.resource_name);
          });
          setResourceMap(rMap);

          const ruMap = new Map<number, string>();
          (Array.isArray(rules) ? rules : []).forEach((r: any) => {
            if (r.id != null) ruMap.set(r.id, r.name || r.rule_name);
          });
          setRuleMap(ruMap);

          const gMap = new Map<number, string>();
          (rqList?.items || []).forEach((q: any) => {
            if (q.id != null) gMap.set(q.id, q.name);
          });
          setGrqMap(gMap);
        },
      )
      .finally(() => setIsLoading(false));
  }, [resolvedRunId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Save handlers ─────────────────────────────────────────────
  const handleStartEditWeights = () => {
    setWeightDraft({
      validation_weight: pipelineWeights?.validation_weight ?? "",
      comparison_weight: pipelineWeights?.comparison_weight ?? "",
      research_query_weight: pipelineWeights?.research_query_weight ?? "",
    });
    setEditingWeights(true);
  };

  const handleSaveWeights = async () => {
    if (!resolvedRunId) return;
    setSavingWeights(true);
    try {
      const payload = {
        validation_weight: Number(weightDraft.validation_weight),
        comparison_weight: Number(weightDraft.comparison_weight),
        research_query_weight: Number(weightDraft.research_query_weight),
      };
      if (pipelineWeights?.id) {
        await updatePipelineWeightConfig(pipelineWeights.id, payload);
      } else {
        await createPipelineWeightConfig({
          run_instance_id: resolvedRunId,
          ...payload,
        });
      }
      showToast("Pipeline weights updated", "success");
      setEditingWeights(false);
      await loadAll();
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update pipeline weights"),
        "error",
      );
    } finally {
      setSavingWeights(false);
    }
  };

  const buildBatchItem = (
    pipeline: PipelineKey,
    runConfig: any,
    severity: string,
    factor: number,
    overrides: Record<string, any> = {},
  ) => {
    const base: any = {
      run_instance_id: resolvedRunId,
      severity,
      factor,
      ...overrides,
    };
    if (runConfig?.id) base.id = runConfig.id;
    if (pipeline === "validation") {
      base.resource_id = runConfig?.resource_id ?? overrides.resource_id;
    } else if (pipeline === "comparison") {
      base.rule_id = runConfig?.rule_id ?? overrides.rule_id;
    } else {
      base.generic_research_query_id =
        runConfig?.generic_research_query_id ??
        overrides.generic_research_query_id;
    }
    return base;
  };

  const callBatch = async (pipeline: PipelineKey, items: any[]) => {
    if (!resolvedRunId) return;
    if (pipeline === "validation") {
      await batchCreateOrUpdateRunScoringConfig(resolvedRunId, items);
    } else if (pipeline === "comparison") {
      await batchCreateOrUpdateComparisonRunScoringConfig(resolvedRunId, items);
    } else {
      await batchCreateOrUpdateResearchQueryRunScoringConfig(
        resolvedRunId,
        items,
      );
    }
  };

  const handleSaveEntity = async (
    pipeline: PipelineKey,
    runConfig: any,
    severity: string,
    factor: number,
  ) => {
    setSavingEntity(true);
    try {
      const item = buildBatchItem(pipeline, runConfig, severity, factor);
      await callBatch(pipeline, [item]);
      showToast("Configuration updated", "success");
      await loadAll();
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update configuration"),
        "error",
      );
    } finally {
      setSavingEntity(false);
    }
  };

  const handleAddEntity = async (
    pipeline: PipelineKey,
    entityId: number,
    severity: string,
    factor: number,
  ) => {
    const overrides: Record<string, any> =
      pipeline === "validation"
        ? { resource_id: entityId }
        : pipeline === "comparison"
          ? { rule_id: entityId }
          : { generic_research_query_id: entityId };
    setSavingEntity(true);
    try {
      const item = buildBatchItem(pipeline, null, severity, factor, overrides);
      await callBatch(pipeline, [item]);
      showToast("Configuration added", "success");
      setAddingPipeline(null);
      await loadAll();
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to add configuration"),
        "error",
      );
    } finally {
      setSavingEntity(false);
    }
  };

  // Available options for "Add" forms (entities not yet configured for this run)
  const validationConfiguredIds = new Set(
    validationRunConfigs.map((c) => c.resource_id),
  );
  const availableResourceOptions = Array.from(resourceMap.entries())
    .filter(([id]) => !validationConfiguredIds.has(id))
    .map(([id, name]) => ({ id, name }));

  const comparisonConfiguredIds = new Set(
    comparisonRunConfigs.map((c) => c.rule_id),
  );
  const availableRuleOptions = Array.from(ruleMap.entries())
    .filter(([id]) => !comparisonConfiguredIds.has(id))
    .map(([id, name]) => ({ id, name }));

  const rqConfiguredIds = new Set(
    rqRunConfigs.map((c) => c.generic_research_query_id),
  );
  const availableQueryOptions = Array.from(grqMap.entries())
    .filter(([id]) => !rqConfiguredIds.has(id))
    .map(([id, name]) => ({ id, name }));

  const validationBaseFields = [
    { label: "Record Impact", key: "impact_of_record_level" },
    { label: "Issue Impact", key: "impact_of_issue_level" },
    { label: "Factor Low", key: "factor_low" },
    { label: "Factor Medium", key: "factor_medium" },
    { label: "Factor High", key: "factor_high" },
  ];
  const comparisonBaseFields = [
    { label: "Blend Ratio", key: "blend_ratio" },
    { label: "Weight: Missing", key: "category_weight_missing" },
    { label: "Weight: Mismatch", key: "category_weight_mismatch" },
    { label: "Weight: Validation", key: "category_weight_validation" },
    { label: "Missing Factor", key: "missing_factor" },
    { label: "Mismatch Factor Low", key: "mismatch_factor_low" },
    { label: "Mismatch Factor Medium", key: "mismatch_factor_medium" },
    { label: "Mismatch Factor High", key: "mismatch_factor_high" },
  ];
  const rqBaseFields = [
    { label: "Record Impact", key: "impact_of_record_level" },
    { label: "Issue Impact", key: "impact_of_issue_level" },
    { label: "Factor Low", key: "factor_low" },
    { label: "Factor Medium", key: "factor_medium" },
    { label: "Factor High", key: "factor_high" },
  ];

  // Base-config resolvers + per-severity factor extractors used by AddEntityForm
  // to prefill fields from the matching base config (or system defaults).
  const resolveValidationBase = useCallback(
    (id: number) => validationBaseByResource.get(id),
    [validationBaseByResource],
  );
  const resolveComparisonBase = useCallback(
    (id: number) => comparisonBaseByRule.get(id),
    [comparisonBaseByRule],
  );
  const resolveRqBase = useCallback(
    (id: number) => rqBaseByQuery.get(id),
    [rqBaseByQuery],
  );

  const factorFromValidationBase = useCallback(
    (base: any, severity: string) => {
      const key = `factor_${severity}`;
      const v = base?.[key];
      return typeof v === "number" ? v : v != null ? Number(v) : undefined;
    },
    [],
  );
  const factorFromComparisonBase = useCallback(
    (base: any, severity: string) => {
      const key = `mismatch_factor_${severity}`;
      const v = base?.[key];
      return typeof v === "number" ? v : v != null ? Number(v) : undefined;
    },
    [],
  );
  const factorFromRqBase = factorFromValidationBase;

  const weightInput = (label: string, key: keyof typeof weightDraft) => (
    <Grid item xs={12} sm={6} md={4}>
      <label className="label-text">{label}</label>
      <TextField
        type="number"
        fullWidth
        size="small"
        value={weightDraft[key]}
        onChange={(e) =>
          setWeightDraft((prev) => ({ ...prev, [key]: e.target.value }))
        }
        className="form-control"
        inputProps={{ step: "0.01", min: 0 }}
      />
    </Grid>
  );

  return (
    <Box>
      {(isLoading || savingWeights || savingEntity) && (
        <Loader isLoading={true} />
      )}

      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <h3>Run Scoring Overview</h3>
            {!editingWeights && (
              <Tooltip title="Edit pipeline weights" arrow>
                <IconButton size="small" onClick={handleStartEditWeights}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        >
          {field("Execution Group Name", runDetail?.run_name)}
          {field("Execution Group ID", resolvedRunId)}
          {field(
            "Status",
            runDetail?.is_active === false ? "Inactive" : "Active",
          )}
          {editingWeights ? (
            <>
              {weightInput("Validation Weight", "validation_weight")}
              {weightInput("Comparison Weight", "comparison_weight")}
              {weightInput("Research Query Weight", "research_query_weight")}
            </>
          ) : (
            <>
              {field("Validation Weight", pipelineWeights?.validation_weight)}
              {field("Comparison Weight", pipelineWeights?.comparison_weight)}
              {field(
                "Research Query Weight",
                pipelineWeights?.research_query_weight,
              )}
            </>
          )}
          {field(
            "Last Modified",
            runDetail?.updated_on
              ? getFormattedDateTime(runDetail.updated_on)
              : "—",
            6,
          )}
        </Grid>
        {editingWeights && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button
              type="button"
              className="btn-orange btn-dark btn-sm"
              variant="contained"
              startIcon={<CloseIcon />}
              onClick={() => setEditingWeights(false)}
              disabled={savingWeights}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="btn-orange btn-sm"
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSaveWeights}
              disabled={savingWeights}
            >
              Save
            </Button>
          </Stack>
        )}
      </Box>

      {/* Validation Pipeline */}
      <Box sx={{ mt: 3 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PipelineHeader
              pipelineKey="validation"
              count={validationRunConfigs.length}
            />
          </AccordionSummary>
          <AccordionDetails>
            {validationRunConfigs.length === 0 ? (
              <Typography sx={{ color: "#999" }}>
                No validation run scoring configs for this run.
              </Typography>
            ) : (
              validationRunConfigs.map((rc) => (
                <EntityBlock
                  key={rc.id}
                  title={
                    resourceMap.get(rc.resource_id) ??
                    `Resource #${rc.resource_id}`
                  }
                  runConfig={rc}
                  baseConfig={validationBaseByResource.get(rc.resource_id)}
                  baseFields={validationBaseFields}
                  saving={savingEntity}
                  onSave={(severity, factor) =>
                    handleSaveEntity("validation", rc, severity, factor)
                  }
                />
              ))
            )}
            {addingPipeline === "validation" ? (
              <AddEntityForm
                entityLabel="Resource"
                options={availableResourceOptions}
                saving={savingEntity}
                onCancel={() => setAddingPipeline(null)}
                onAdd={(id, sev, factor) =>
                  handleAddEntity("validation", id, sev, factor)
                }
                resolveBaseConfig={resolveValidationBase}
                getFactorForSeverity={factorFromValidationBase}
              />
            ) : (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  type="button"
                  className="btn-orange btn-sm plus-btn-sm"
                  variant="contained"
                  onClick={() => setAddingPipeline("validation")}
                  disabled={availableResourceOptions.length === 0}
                >
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> Add Resource
                  Override
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Comparison Pipeline */}
      <Box sx={{ mt: 2 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PipelineHeader
              pipelineKey="comparison"
              count={comparisonRunConfigs.length}
            />
          </AccordionSummary>
          <AccordionDetails>
            {comparisonRunConfigs.length === 0 ? (
              <Typography sx={{ color: "#999" }}>
                No comparison run scoring configs for this run.
              </Typography>
            ) : (
              comparisonRunConfigs.map((rc) => (
                <EntityBlock
                  key={rc.id}
                  title={ruleMap.get(rc.rule_id) ?? `Rule #${rc.rule_id}`}
                  runConfig={rc}
                  baseConfig={comparisonBaseByRule.get(rc.rule_id)}
                  baseFields={comparisonBaseFields}
                  saving={savingEntity}
                  onSave={(severity, factor) =>
                    handleSaveEntity("comparison", rc, severity, factor)
                  }
                />
              ))
            )}
            {addingPipeline === "comparison" ? (
              <AddEntityForm
                entityLabel="Rule"
                options={availableRuleOptions}
                saving={savingEntity}
                onCancel={() => setAddingPipeline(null)}
                onAdd={(id, sev, factor) =>
                  handleAddEntity("comparison", id, sev, factor)
                }
                resolveBaseConfig={resolveComparisonBase}
                getFactorForSeverity={factorFromComparisonBase}
              />
            ) : (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  type="button"
                  className="btn-orange btn-sm plus-btn-sm"
                  variant="contained"
                  onClick={() => setAddingPipeline("comparison")}
                  disabled={availableRuleOptions.length === 0}
                >
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> Add Rule Override
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Research Query Pipeline */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <PipelineHeader
              pipelineKey="research_query"
              count={rqRunConfigs.length}
            />
          </AccordionSummary>
          <AccordionDetails>
            {rqRunConfigs.length === 0 ? (
              <Typography sx={{ color: "#999" }}>
                No research query run scoring configs for this run.
              </Typography>
            ) : (
              rqRunConfigs.map((rc) => (
                <EntityBlock
                  key={rc.id}
                  title={
                    grqMap.get(rc.generic_research_query_id) ??
                    `Query #${rc.generic_research_query_id}`
                  }
                  runConfig={rc}
                  baseConfig={rqBaseByQuery.get(rc.generic_research_query_id)}
                  baseFields={rqBaseFields}
                  saving={savingEntity}
                  onSave={(severity, factor) =>
                    handleSaveEntity("research_query", rc, severity, factor)
                  }
                />
              ))
            )}
            {addingPipeline === "research_query" ? (
              <AddEntityForm
                entityLabel="Research Query"
                options={availableQueryOptions}
                saving={savingEntity}
                onCancel={() => setAddingPipeline(null)}
                onAdd={(id, sev, factor) =>
                  handleAddEntity("research_query", id, sev, factor)
                }
                resolveBaseConfig={resolveRqBase}
                getFactorForSeverity={factorFromRqBase}
              />
            ) : (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  type="button"
                  className="btn-orange btn-sm plus-btn-sm"
                  variant="contained"
                  onClick={() => setAddingPipeline("research_query")}
                  disabled={availableQueryOptions.length === 0}
                >
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> Add Query
                  Override
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default RunScoringDetailedOverview;
